import { useEffect, useRef, useState } from 'react';
import { api, isAuthPending } from '../lib/api';
import { eventDateEditError, eventInputForAllDay, isoToLocalInput, resolveEventDateEdit } from '../lib/datetime';
import { isAllDayEvent } from '../lib/eventDates';
import type { ExtractedEvent, EventStatus, Venue, EventSeries } from '../types';

interface Props {
  event: ExtractedEvent | null;
  venues: Venue[];
  /** Optional series list for the series picker. Defaults to []. */
  seriesList?: EventSeries[];
  onClose: () => void;
  onSaved: (e: ExtractedEvent) => void;
  /** Optional admin action: deactivate the event's venue (hides all of its events). */
  onHideVenue?: (event: ExtractedEvent) => void;
  /** Optional admin action: dismiss + deactivate the event's series (hides all of its events). */
  onHideSeries?: (event: ExtractedEvent) => void;
  /** Optional admin action: mark this event as rejected (hides from public list). */
  onReject?: (event: ExtractedEvent) => void;
}

interface FormState {
  title: string;
  description: string;
  aiSummary: string;
  startLocal: string; // NY datetime-local, or an all-day source date label
  endLocal: string;
  isAllDay: boolean;
  locationText: string;
  venueId: string;
  seriesId: string;
  ticketUrl: string;
  imageUrl: string;
  eventType: string;
  tags: string; // comma-separated
  performers: string; // comma-separated
  priceInfo: string;
  isFreeEntry: boolean;
  ageRestriction: string;
  organizer: string;
  recurringPattern: string;
  isConfirmedEvent: boolean;
  status: EventStatus;
}

const STATUS_OPTIONS: EventStatus[] = ['pending', 'approved', 'published', 'rejected', 'duplicate'];
const RECURRING_OPTIONS = ['', 'one-time', 'weekly', 'biweekly', 'monthly'];
const AGE_OPTIONS = ['', 'all-ages', '18+', '21+'];

function toForm(e: ExtractedEvent): FormState {
  const allDay = isAllDayEvent(e);
  return {
    title: e.title ?? '',
    description: e.description ?? '',
    aiSummary: e.aiSummary ?? '',
    startLocal: isoToLocalInput(e.startUtc, allDay),
    endLocal: isoToLocalInput(e.endUtc, allDay),
    isAllDay: allDay,
    locationText: e.locationText ?? '',
    venueId: e.venueId ?? '',
    seriesId: e.seriesId ?? '',
    ticketUrl: e.ticketUrl ?? '',
    imageUrl: e.imageUrl ?? '',
    eventType: e.eventType ?? '',
    tags: (e.tags ?? []).join(', '),
    performers: (e.performers ?? []).join(', '),
    priceInfo: e.priceInfo ?? '',
    isFreeEntry: !!e.isFreeEntry,
    ageRestriction: e.ageRestriction ?? '',
    organizer: e.organizer ?? '',
    recurringPattern: e.recurringPattern ?? '',
    isConfirmedEvent: e.isConfirmedEvent !== false,
    status: e.status,
  };
}

function splitCsv(s: string): string[] | undefined {
  const arr = s.split(',').map((v) => v.trim()).filter(Boolean);
  return arr.length ? arr : undefined;
}

export default function EventEditModal({ event, venues, seriesList = [], onClose, onSaved, onHideVenue, onHideSeries, onReject }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [form, setForm] = useState<FormState | null>(event ? toForm(event) : null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(event ? toForm(event) : null);
    setError(null);
  }, [event]);

  useEffect(() => {
    if (!event) return;
    const prev = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prev?.focus?.();
    };
  }, [event, onClose]);

  if (!event || !form) return null;

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  };
  const setAllDay = (isAllDay: boolean) => {
    setForm((f) => f ? {
      ...f, isAllDay,
      startLocal: eventInputForAllDay(f.startLocal, isAllDay),
      endLocal: eventInputForAllDay(f.endLocal, isAllDay),
    } : f);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    const temporal = resolveEventDateEdit(form, event);
    if (!temporal.ok) {
      setError(eventDateEditError(temporal));
      return;
    }
    const { startUtc, endUtc } = temporal.value;
    const tagsArr = splitCsv(form.tags);
    const performersArr = splitCsv(form.performers);
    const venue = form.venueId ? venues.find((v) => v.rowKey === form.venueId) : undefined;
    const series = form.seriesId ? seriesList.find((s) => s.rowKey === form.seriesId) : undefined;    setSaving(true);
    setError(null);
    try {
      const patch = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        aiSummary: form.aiSummary.trim() || undefined,
        startUtc,
        endUtc,
        isAllDay: form.isAllDay,
        locationText: form.locationText.trim() || undefined,
        venueId: form.venueId || undefined,
        venueName: venue?.name ?? (form.venueId ? event.venueName : undefined),
        seriesId: form.seriesId || undefined,
        ticketUrl: form.ticketUrl.trim() || undefined,
        imageUrl: form.imageUrl.trim() || undefined,
        eventType: form.eventType.trim() || undefined,
        tagsJson: tagsArr ? JSON.stringify(tagsArr) : undefined,
        performersJson: performersArr ? JSON.stringify(performersArr) : undefined,
        priceInfo: form.priceInfo.trim() || undefined,
        isFreeEntry: form.isFreeEntry,
        ageRestriction: form.ageRestriction || undefined,
        organizer: form.organizer.trim() || undefined,
        recurringPattern: form.recurringPattern || undefined,
        isConfirmedEvent: form.isConfirmedEvent,
        status: form.status,
      };
      const updated = await api.patch<ExtractedEvent>(
        `/api/mgmt/events/${encodeURIComponent(event.rowKey)}`,
        patch
      );
      // Backend returns raw entity without parsed tags/performers; mirror what
      // we sent so the UI updates immediately without a refetch.
      onSaved({
        ...event,
        ...updated,
        endUtc: endUtc === null ? undefined : updated.endUtc ?? endUtc,
        tags: tagsArr,
        performers: performersArr,
        seriesName: series?.name ?? (form.seriesId ? event.seriesName : undefined),
        seriesType: series?.partitionKey ?? (form.seriesId ? event.seriesType : undefined),
      });
      onClose();
    } catch (e) {
      if (!isAuthPending(e)) setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mh-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="event-edit-title"
      onClick={onClose}
    >
      <div className="mh-modal-dialog" onClick={(e) => e.stopPropagation()}>
        <header className="d-flex align-items-start gap-2 p-3 border-bottom">
          <h2 id="event-edit-title" className="h5 mb-0 flex-grow-1">Edit event</h2>
          {onHideVenue && event.venueId && (event.venueName || event.locationText) && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => onHideVenue(event)}
              aria-label={`Decline venue ${event.venueName ?? event.locationText}`}
              title={`Decline venue "${event.venueName ?? event.locationText}" — hides all of its events from the public list. Re-approve from Manage venues.`}
            >
              <i className="bi bi-eye-slash me-1" aria-hidden="true" />
              Hide venue
            </button>
          )}
          {onHideSeries && event.seriesId && event.seriesName && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => onHideSeries(event)}
              aria-label={`Dismiss series ${event.seriesName}`}
              title={`Dismiss series "${event.seriesName}" (hides all of its events)`}
            >
              <i className="bi bi-collection me-1" aria-hidden="true" />
              Hide series
            </button>
          )}
          {onReject && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => onReject(event)}
              aria-label={`Reject ${event.title}`}
              title="Reject event (hides from public list)"
            >
              <i className="bi bi-x-circle me-1" aria-hidden="true" />
              Reject
            </button>
          )}
          <button ref={closeRef} type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </header>
        <form
          className="p-3"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          {error && <div className="alert alert-danger" role="alert">{error}</div>}

          <div className="row g-3">
            <div className="col-md-9">
              <label htmlFor="ev-title" className="form-label small fw-semibold">Title *</label>
              <input
                id="ev-title"
                type="text"
                className="form-control"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                required
                aria-required="true"
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="ev-status" className="form-label small fw-semibold">Status</label>
              <select
                id="ev-status"
                className="form-select"
                value={form.status}
                onChange={(e) => setField('status', e.target.value as EventStatus)}
              >
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="col-md-6">
              <label htmlFor="ev-start" className="form-label small fw-semibold">
                {form.isAllDay ? 'Start date *' : 'Start *'}
              </label>
              <input
                id="ev-start"
                type={form.isAllDay ? 'date' : 'datetime-local'}
                step={form.isAllDay ? undefined : 'any'}
                className="form-control"
                value={form.startLocal}
                onChange={(e) => setField('startLocal', e.target.value)}
                required
                aria-required="true"
                aria-describedby={form.isAllDay ? 'ev-temporal-zone' : undefined}
              />
              {form.isAllDay && (
                <p id="ev-temporal-zone" className="small text-muted mb-0 mt-1">
                  All-day dates stay on the source calendar date.
                </p>
              )}
            </div>
            <div className="col-md-6">
              <label htmlFor="ev-end" className="form-label small fw-semibold">
                {form.isAllDay ? 'End date' : 'End'}
              </label>
              <input
                id="ev-end"
                type={form.isAllDay ? 'date' : 'datetime-local'}
                step={form.isAllDay ? undefined : 'any'}
                className="form-control"
                value={form.endLocal}
                onChange={(e) => setField('endLocal', e.target.value)}
              />
              <div className="form-check mt-1">
                <input
                  id="ev-allday"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.isAllDay}
                  onChange={(e) => setAllDay(e.target.checked)}
                />
                <label htmlFor="ev-allday" className="form-check-label small">All-day event</label>
              </div>
            </div>

            <div className="col-md-6">
              <label htmlFor="ev-venue" className="form-label small fw-semibold">Venue</label>
              <select
                id="ev-venue"
                className="form-select"
                value={form.venueId}
                onChange={(e) => setField('venueId', e.target.value)}
              >
                <option value="">— No venue —</option>
                {/* The venue list is fetched lazily when this modal first opens,
                    so it can briefly be empty. Render the event's current venue
                    as a stand-in option meanwhile, otherwise the select would
                    read "No venue" for an event that has one. */}
                {venues.length === 0 && form.venueId && event.venueName && (
                  <option value={form.venueId}>{event.venueName}</option>
                )}
                {venues.map((v) => (
                  <option key={v.rowKey} value={v.rowKey}>{v.name}{v.partitionKey ? ` (${v.partitionKey})` : ''}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label htmlFor="ev-series" className="form-label small fw-semibold">Series</label>
              <select
                id="ev-series"
                className="form-select"
                value={form.seriesId}
                onChange={(e) => setField('seriesId', e.target.value)}
                disabled={seriesList.length === 0}
              >
                <option value="">— No series —</option>
                {seriesList.length === 0 && form.seriesId && event.seriesName && (
                  <option value={form.seriesId}>{event.seriesName}</option>
                )}
                {seriesList.map((s) => (
                  <option key={s.rowKey} value={s.rowKey}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label htmlFor="ev-loc" className="form-label small fw-semibold">Location text</label>
              <input
                id="ev-loc"
                type="text"
                className="form-control"
                value={form.locationText}
                onChange={(e) => setField('locationText', e.target.value)}
                placeholder="Free-form fallback when no venue is set"
              />
            </div>

            <div className="col-12">
              <label htmlFor="ev-desc" className="form-label small fw-semibold">Description (verbatim)</label>
              <textarea
                id="ev-desc"
                className="form-control"
                rows={5}
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
              />
            </div>
            <div className="col-12">
              <label htmlFor="ev-ai" className="form-label small fw-semibold">AI summary</label>
              <textarea
                id="ev-ai"
                className="form-control"
                rows={2}
                value={form.aiSummary}
                onChange={(e) => setField('aiSummary', e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="ev-ticket" className="form-label small fw-semibold">Ticket / details URL</label>
              <input
                id="ev-ticket"
                type="url"
                className="form-control"
                value={form.ticketUrl}
                onChange={(e) => setField('ticketUrl', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="ev-img" className="form-label small fw-semibold">Image URL</label>
              <input
                id="ev-img"
                type="url"
                className="form-control"
                value={form.imageUrl}
                onChange={(e) => setField('imageUrl', e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <label htmlFor="ev-type" className="form-label small fw-semibold">Event type</label>
              <input
                id="ev-type"
                type="text"
                className="form-control"
                value={form.eventType}
                onChange={(e) => setField('eventType', e.target.value)}
                placeholder="live-music, art-show, trivia…"
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="ev-rec" className="form-label small fw-semibold">Recurring</label>
              <select
                id="ev-rec"
                className="form-select"
                value={form.recurringPattern}
                onChange={(e) => setField('recurringPattern', e.target.value)}
              >
                {RECURRING_OPTIONS.map((r) => <option key={r || 'none'} value={r}>{r || '—'}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label htmlFor="ev-age" className="form-label small fw-semibold">Age restriction</label>
              <select
                id="ev-age"
                className="form-select"
                value={form.ageRestriction}
                onChange={(e) => setField('ageRestriction', e.target.value)}
              >
                {AGE_OPTIONS.map((a) => <option key={a || 'none'} value={a}>{a || '—'}</option>)}
              </select>
            </div>

            <div className="col-md-6">
              <label htmlFor="ev-tags" className="form-label small fw-semibold">Tags</label>
              <input
                id="ev-tags"
                type="text"
                className="form-control"
                value={form.tags}
                onChange={(e) => setField('tags', e.target.value)}
                placeholder="comma-separated"
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="ev-perf" className="form-label small fw-semibold">Performers</label>
              <input
                id="ev-perf"
                type="text"
                className="form-control"
                value={form.performers}
                onChange={(e) => setField('performers', e.target.value)}
                placeholder="comma-separated"
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="ev-org" className="form-label small fw-semibold">Organizer</label>
              <input
                id="ev-org"
                type="text"
                className="form-control"
                value={form.organizer}
                onChange={(e) => setField('organizer', e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="ev-price" className="form-label small fw-semibold">Price info</label>
              <input
                id="ev-price"
                type="text"
                className="form-control"
                value={form.priceInfo}
                onChange={(e) => setField('priceInfo', e.target.value)}
                placeholder="e.g. $10 cover, free w/ RSVP"
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <div className="form-check">
                <input
                  id="ev-free"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.isFreeEntry}
                  onChange={(e) => setField('isFreeEntry', e.target.checked)}
                />
                <label htmlFor="ev-free" className="form-check-label small">Free entry</label>
              </div>
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <div className="form-check">
                <input
                  id="ev-confirmed"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.isConfirmedEvent}
                  onChange={(e) => setField('isConfirmedEvent', e.target.checked)}
                />
                <label htmlFor="ev-confirmed" className="form-check-label small">
                  Confirmed event (uncheck for promo/teaser only)
                </label>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
